import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import useCurrencies from "../../hooks/useCurrencies";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import PurchaseOrderTabBar from "./purchaseOrderTabBar";
import swal from "sweetalert";

const UpdatePurchaseOrderPayments = () => {
  const { poId } = useParams();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { currencies } = useCurrencies();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [poData, setPoData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  // Add payment form
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentReference, setPaymentReference] = useState("");
  const [currency, setCurrency] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  // Fetch purchase order data
  const fetchPurchaseOrder = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);
      if (response.data && response.data.data) {
        setPoData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching purchase order:", error);
    }
  }, [poId, compID]);

  // Calculate total paid amount from all payments
  const calculateTotalPaidAmount = useCallback(async () => {
    if (!poId || !compID) return 0;

    try {
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_PAYMENTS}?poId=${poId}&compId=${compID}`);
      const paymentsData = response.data?.data || response.data || [];
      
      // Sum all payment amounts (only count completed/processed payments, or all if status doesn't matter)
      const total = paymentsData.reduce((sum, payment) => {
        const paymentAmount = parseFloat(payment.amount || payment.Amount || 0);
        const paymentStatus = payment.paymentStatus || payment.PaymentStatus || "Pending";
        // Only count completed/processed payments, or count all if you want to include pending
        // Adjust this logic based on your business rules
        if (paymentStatus === "Failed" || paymentStatus === "Cancelled") {
          return sum; // Don't count failed or cancelled payments
        }
        return sum + paymentAmount;
      }, 0);

      return total;
    } catch (error) {
      console.error("Error calculating total paid amount:", error);
      return 0;
    }
  }, [poId, compID]);

  // Update purchase order paid amount
  const updatePurchaseOrderPaidAmount = useCallback(async (paidAmount) => {
    if (!poId || !compID) return;

    try {
      // Fetch latest PO data if not available
      let currentPoData = poData;
      if (!currentPoData) {
        const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);
        if (response.data && response.data.data) {
          currentPoData = response.data.data;
        } else {
          console.error("Could not fetch PO data for update");
          return;
        }
      }

      // Calculate total amount including other expenses
      const poAmount = parseFloat(currentPoData.pO_Amount || currentPoData.po_Amount || 0);
      const otherExpenses = parseFloat(currentPoData.pO_OtherExpenses || currentPoData.otherExpenses || 0);
      const totalAmount = poAmount + otherExpenses;

      // Determine payment status: if paid amount >= total amount (including expenses), set to "Paid"
      let paymentStatus = currentPoData.pO_Paymentstatus || "Unpaid";
      if (paidAmount >= totalAmount && totalAmount > 0) {
        paymentStatus = "Paid";
      } else if (paidAmount > 0 && paidAmount < totalAmount) {
        paymentStatus = "Partially Paid";
      } else {
        paymentStatus = "Unpaid";
      }

      const updatePayload = {
        PO_ID: parseInt(poId),
        PO_Name: currentPoData.pO_Name || "",
        DeliveryDueDate: currentPoData.deliveryDueDate || new Date().toISOString(),
        PO_DeliveryType: currentPoData.pO_DeliveryType || "Standard",
        SupplierID: currentPoData.supplierID || 0,
        SupplierName: currentPoData.supplierName || "",
        PO_status: currentPoData.pO_status || "Draft",
        PO_Paymentstatus: paymentStatus,
        PO_Quantity: currentPoData.pO_Quantity || "0",
        PO_Amount: poAmount,
        PO_PaidAmount: paidAmount,
        OtherExpenses: otherExpenses,
        PO_IsSendEmail: currentPoData.pO_IsSendEmail || false,
        DeliverStockToCompID: currentPoData.deliverStockToCompID || 0,
        StaffUserName: currentUserName || "System",
        CompID: compID,
        CreatedBy: currentPoData.createdBy || currentPoData.CreatedBy || currentUserName || "System",
        ModifiedBy: currentUserName || "System",
        PurchaseOrderPayments: [],
        PurchaseOrderProductLists: []
      };

      console.log("Updating PO paid amount:", paidAmount, "Total amount (with expenses):", totalAmount, "Payment status:", paymentStatus, "for PO_ID:", poId);
      await axiosInstance.put(APIs.PUT_UPDATE_PURCHASE_ORDER, updatePayload);
      
      // Refresh PO data to get updated paid amount and payment status
      await fetchPurchaseOrder();
    } catch (error) {
      console.error("Error updating purchase order paid amount:", error);
      console.error("Error details:", error.response?.data || error.message);
      // Don't show error toast as this is a background operation
    }
  }, [poId, compID, poData, fetchPurchaseOrder, currentUserName]);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_PAYMENTS}?poId=${poId}&compId=${compID}`);
      if (response.data && response.data.data) {
        setPayments(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      showErrorToast("Failed to load payments");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [poId, compID]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchPurchaseOrder(), fetchPayments()]);
    };
    loadData();
  }, [fetchPurchaseOrder, fetchPayments]);

  // Reset form
  const resetForm = () => {
    setAmount("");
    setPaymentMethod("Cash");
    setPaymentReference("");
    setCurrency("");
    setPaymentDate("");
    setNotes("");
  };

  // Handle add payment
  const handleAddPayment = () => {
    const poAmt = parseFloat(poData?.pO_Amount || poData?.po_Amount || 0);
    const expenses = parseFloat(poData?.otherExpenses || poData?.pO_OtherExpenses || 0);
    const poTotal = poAmt + expenses;
    const paidSoFar = payments.reduce((sum, payment) => {
      const paymentAmount = parseFloat(payment.amount || payment.Amount || 0);
      const paymentStatus = payment.paymentStatus || payment.PaymentStatus || "Pending";
      if (paymentStatus === "Failed" || paymentStatus === "Cancelled") return sum;
      return sum + paymentAmount;
    }, 0);
    const remaining = Math.round((poTotal - paidSoFar) * 100) / 100;

    if (remaining <= 0) {
      showErrorToast("This purchase order is already fully paid. No further payment can be added.");
      return;
    }

    resetForm();
    const today = new Date().toISOString().split("T")[0];
    setPaymentDate(today);
    setCurrency(compCurrency || "");
    setAmount(remaining > 0 ? remaining.toFixed(2) : "");
    setShowAddPaymentModal(true);
  };

  // Handle delete payment
  const handleDeletePayment = async (payment) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete this payment of $${payment.amount?.toFixed(2)}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setLoading(true);
      await axiosInstance.delete(`${APIs.DELETE_PURCHASE_ORDER_PAYMENT}?paymentId=${payment.paymentID}&compId=${compID}`);
      showSuccessToast("Payment deleted successfully");
      await fetchPayments(); // Refresh the list
      
      // Calculate and update total paid amount
      const totalPaidAmount = await calculateTotalPaidAmount();
      console.log("Calculated total paid amount after deleting payment:", totalPaidAmount);
      await updatePurchaseOrderPaidAmount(totalPaidAmount);
    } catch (error) {
      console.error("Error deleting payment:", error);
      showErrorToast("Failed to delete payment");
    } finally {
      setLoading(false);
    }
  };

  // Handle update payment status
  const handleUpdatePaymentStatus = async (paymentId, newStatus) => {
    try {
      setLoading(true);
      await axiosInstance.put(`${APIs.PUT_UPDATE_PAYMENT_STATUS}?paymentId=${paymentId}&status=${newStatus}&compId=${compID}`);
      showSuccessToast("Payment status updated successfully");
      await fetchPayments(); // Refresh the list
      
      // Calculate and update total paid amount (status change might affect if payment counts)
      const totalPaidAmount = await calculateTotalPaidAmount();
      console.log("Calculated total paid amount after updating payment status:", totalPaidAmount);
      await updatePurchaseOrderPaidAmount(totalPaidAmount);
    } catch (error) {
      console.error("Error updating payment status:", error);
      showErrorToast("Failed to update payment status");
    } finally {
      setLoading(false);
    }
  };

  // Handle add payment submit (used by CustomModal onSubmit)
  const handleAddPaymentSubmit = useCallback(async () => {
    if (!amount || !paymentDate) {
      showErrorToast("Please fill in amount and payment date");
      return;
    }
    if (!currency) {
      showErrorToast("Please select a currency");
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      showErrorToast("Payment amount must be greater than zero");
      return;
    }

    const poAmt = parseFloat(poData?.pO_Amount || poData?.po_Amount || 0);
    const expenses = parseFloat(poData?.otherExpenses || poData?.pO_OtherExpenses || 0);
    const poTotal = Math.round((poAmt + expenses) * 100) / 100;
    const paidSoFar = payments.reduce((sum, payment) => {
      const amt = parseFloat(payment.amount || payment.Amount || 0);
      const status = payment.paymentStatus || payment.PaymentStatus || "Pending";
      if (status === "Failed" || status === "Cancelled") return sum;
      return sum + amt;
    }, 0);
    const remaining = Math.round((poTotal - paidSoFar) * 100) / 100;

    if (paymentAmount > remaining + 0.001) {
      showErrorToast(
        `Payment amount cannot exceed the remaining balance of ${remaining.toFixed(2)}. PO total is ${poTotal.toFixed(2)}.`
      );
      return;
    }

    try {
      setLoading(true);

      const paymentData = {
        PO_ID: parseInt(poId),
        CompID: compID,
        Amount: paymentAmount,
        PaymentMethod: paymentMethod,
        PaymentReference: paymentReference || "",
        Currency: currency,
        PaymentStatus: paymentMethod === "Cash" ? "Completed" : "Pending",
        PaymentDate: new Date(paymentDate).toISOString(),
        Notes: notes || "",
        CreatedBy: currentUserName || "System",
        PaymentBy: currentUserName || "System"
      };

      await axiosInstance.post(APIs.POST_ADD_PURCHASE_ORDER_PAYMENT, paymentData);

      showSuccessToast("Payment added successfully");
      setShowAddPaymentModal(false);
      resetForm();
      await fetchPayments();

      const totalPaidAmount = await calculateTotalPaidAmount();
      await updatePurchaseOrderPaidAmount(totalPaidAmount);
    } catch (error) {
      console.error("Error adding payment:", error);
      showErrorToast(error.response?.data?.message || "Failed to add payment");
    } finally {
      setLoading(false);
    }
  }, [poId, compID, amount, paymentDate, paymentMethod, paymentReference, currency, notes, currentUserName, poData, payments, fetchPayments, calculateTotalPaidAmount, updatePurchaseOrderPaidAmount]);

  // Calculate total paid amount
  const totalPaid = payments.reduce((sum, payment) => {
    const paymentAmount = parseFloat(payment.amount || payment.Amount || 0);
    const paymentStatus = payment.paymentStatus || payment.PaymentStatus || "Pending";
    // Only count completed/processed payments, or count all if you want to include pending
    if (paymentStatus === "Failed" || paymentStatus === "Cancelled") {
      return sum; // Don't count failed or cancelled payments
    }
    return sum + paymentAmount;
  }, 0);
  
  // Calculate total amount including other expenses
  const poAmount = parseFloat(poData?.pO_Amount || poData?.po_Amount || 0);
  const otherExpenses = parseFloat(poData?.pO_OtherExpenses || poData?.otherExpenses || 0);
  const totalAmount = poAmount + otherExpenses;
  const remainingAmount = totalAmount - totalPaid;

  if (initialLoading) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/purchaseOrder/managePurchaseOrders">Purchase Orders</Link>
          </li>
          <li className="breadcrumb-item active">Purchase Order Payments</li>
        </ol>
        <h1 className="page-header mb-0">
          {poData ? `PO-${poData.pO_ID || poData.po_ID}: ${poData.pO_Name || poData.po_Name} - Payments` : "Purchase Order Payments"}
        </h1>
      </div>
      <div className="ms-auto d-flex gap-2 align-items-center">
        <button
          onClick={handleAddPayment}
          className="btn px-4"
          disabled={remainingAmount <= 0}
          style={{ 
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`,
            cursor: remainingAmount <= 0 ? 'not-allowed' : 'pointer',
            opacity: remainingAmount <= 0 ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (remainingAmount <= 0) return;
            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
          }}
        >
          <i className="fa fa-plus" style={{ fontSize: "12px" }}></i>
          Add Payment
        </button>
      </div>
    </div>
  );

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}

      {/* Tab Navigation */}
      {poId && <PurchaseOrderTabBar poId={poId} />}

      {/* Summary Cards */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div
            className="card"
            style={{
              borderRadius: "10px",
              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
              border: isDarkMode ? "1px solid #404040" : `1px solid ${themeColor}`,
            }}
          >
            <div className="card-body text-center">
              <h5 className="card-title" style={{ color: themeColor }}>Total Amount</h5>
              <h3 style={{ color: themeColor }}>${totalAmount.toFixed(2)}</h3>
              {otherExpenses > 0 && (
                <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  (PO: ${poAmount.toFixed(2)} + Expenses: ${otherExpenses.toFixed(2)})
                </small>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div
            className="card"
            style={{
              borderRadius: "10px",
              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
              border: isDarkMode ? "1px solid #404040" : "1px solid #28a745",
            }}
          >
            <div className="card-body text-center">
              <h5 className="card-title" style={{ color: "#28a745" }}>Total Paid</h5>
              <h3 style={{ color: "#28a745" }}>${totalPaid.toFixed(2)}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div
            className="card"
            style={{
              borderRadius: "10px",
              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
              border: isDarkMode ? "1px solid #404040" : `1px solid ${remainingAmount > 0 ? "#ffc107" : "#28a745"}`,
            }}
          >
            <div className="card-body text-center">
              <h5 className="card-title" style={{ color: remainingAmount > 0 ? "#ffc107" : "#28a745" }}>
                {remainingAmount > 0 ? "Remaining" : "Status"}
              </h5>
              <h3 style={{ color: remainingAmount > 0 ? "#ffc107" : "#28a745" }}>
                {remainingAmount > 0 ? `$${remainingAmount.toFixed(2)}` : "Fully Paid"}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <Panel className="mb-4">
        <PanelHeader
          style={{
            backgroundColor: themeColor,
            color: "white",
            border: "none",
            borderRadius: "10px 10px 0 0",
            padding: "15px 20px",
            fontWeight: "600",
            fontSize: "16px",
          }}
        >
          <i className="fa fa-credit-card me-2"></i>
          Payment History - {poData ? `PO-${poData.pO_ID || poData.po_ID}` : "Purchase Order"}
        </PanelHeader>
        <PanelBody
          style={{
            padding: "0",
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            borderRadius: "0 0 10px 10px",
            border: `1px solid ${themeColor}20`,
            overflow: "hidden",
          }}
        >
          {payments.length === 0 ? (
            <div
              className="text-center py-5 px-4"
              style={{
                color: isDarkMode ? "#adb5bd" : "#6c757d",
                backgroundColor: isDarkMode ? "rgba(0,0,0,0.1)" : "#f8f9fa",
                margin: "20px",
                borderRadius: "10px",
                border: isDarkMode ? "1px dashed #404040" : "1px dashed #dee2e6",
              }}
            >
              <i className="fa fa-credit-card mb-2" style={{ fontSize: "32px", display: "block" }}></i>
              <p className="mb-0" style={{ fontSize: "15px" }}>No payments recorded for this purchase order.</p>
              <p className="mb-0 mt-1 small">Add a payment using the button above.</p>
            </div>
          ) : (
            <div
              className="table-responsive table-container"
              style={{
                minHeight: "120px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <table
                className="table table-hover mb-0"
                style={{
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: isDarkMode ? "#3c4043" : "#f8f9fa",
                      borderBottom: `2px solid ${themeColor}`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle" }}>Date</th>
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle" }}>Method</th>
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle" }}>Reference</th>
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle" }}>Amount</th>
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle", minWidth: "140px" }}>Status</th>
                    <th style={{ padding: "14px 16px", fontWeight: "600", verticalAlign: "middle", width: "90px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr
                      key={payment.paymentID}
                      style={{
                        borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#3c4043" : "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : "N/A"}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>{payment.paymentMethod || "—"}</td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>{payment.paymentReference || "N/A"}</td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", fontWeight: "600" }}>
                        {payment.currency || payment.Currency || "USD"} {parseFloat(payment.amount || payment.Amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <select
                          className="form-select form-select-sm"
                          value={payment.paymentStatus || payment.PaymentStatus || "Pending"}
                          onChange={(e) => handleUpdatePaymentStatus(payment.paymentID, e.target.value)}
                          disabled={loading}
                          style={{
                            minWidth: "120px",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            fontSize: "13px",
                            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                          }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Processed">Processed</option>
                          <option value="Completed">Completed</option>
                          <option value="Failed">Failed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", textAlign: "center" }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => handleDeletePayment(payment)}
                          disabled={loading}
                          title="Delete payment"
                          style={{
                            padding: "6px 10px",
                            borderRadius: "6px",
                            border: "1px solid #dc3545",
                            backgroundColor: "transparent",
                            color: "#dc3545",
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) {
                              e.currentTarget.style.backgroundColor = "#dc3545";
                              e.currentTarget.style.color = "#fff";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                            e.currentTarget.style.color = "#dc3545";
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Add Payment Modal - CustomModal */}
      <CustomModal
        isOpen={showAddPaymentModal}
        onClose={() => {
          setShowAddPaymentModal(false);
          resetForm();
        }}
        title={`Add Payment - PO-${poId}`}
        onSubmit={handleAddPaymentSubmit}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText={ "Submit"}
        submitButtonDisabled={loading}
        isCentered={true}
        modalSize="modal-lg"
        minHeight="280px"
        maxHeight="90vh"
      >
        <div className="row g-3">
          <div className="col-12">
            <div
              className="alert mb-0 py-2"
              style={{
                backgroundColor: isDarkMode ? "#3c4043" : "#e8f4fd",
                color: isDarkMode ? "#ffffff" : "#0c5460",
                border: isDarkMode ? "1px solid #404040" : "1px solid #bee5eb",
                borderRadius: "8px",
              }}
            >
              Remaining balance: <strong>{remainingAmount.toFixed(2)}</strong>
              {" "}(PO total {totalAmount.toFixed(2)} − paid {totalPaid.toFixed(2)})
            </div>
          </div>
          <div className="col-md-6">
            <FormInput
              label="Amount *"
              type="number"
              step="0.01"
              min="0.01"
              max={remainingAmount > 0 ? remainingAmount.toFixed(2) : undefined}
              value={amount}
              onchange={(e) => {
                const val = e.target.value;
                const num = parseFloat(val);
                if (val !== "" && !isNaN(num) && num > remainingAmount) {
                  setAmount(remainingAmount.toFixed(2));
                  showErrorToast(`Amount cannot exceed remaining balance of ${remainingAmount.toFixed(2)}`);
                  return;
                }
                setAmount(val);
              }}
              placeHolder="0.00"
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Payment Date *"
              type="date"
              value={paymentDate}
              onchange={(e) => setPaymentDate(e.target.value)}
              required
            />
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
                Payment Method
              </label>
              <select
                className="form-select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#000000",
                  padding: "8px 12px",
                }}
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Wire Transfer">Wire Transfer</option>
                <option value="PayPal">PayPal</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <FormInput
              label="Payment Reference"
              type="text"
              value={paymentReference}
              onchange={(e) => setPaymentReference(e.target.value)}
              placeHolder="Check #, Transaction ID, etc."
            />
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
                Currency
              </label>
              <Select
                options={currencies.map((c) => ({ value: c.value, label: c.text }))}
                value={currencies
                  .filter((c) => c.value === currency)
                  .map((c) => ({ value: c.value, label: c.text }))[0] || null}
                onChange={(option) => setCurrency(option ? option.value : "")}
                placeholder="Select Currency"
                isClearable
                styles={dropdownStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
              Notes
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              style={{
                borderRadius: "8px",
                border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                background: isDarkMode ? "#2d3436" : "#F9F9F9",
                color: isDarkMode ? "#ffffff" : "#000000",
                padding: "8px 12px",
              }}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default UpdatePurchaseOrderPayments;